import React, { useEffect, useState, useCallback, useContext } from "react";
import { FormattedMessage } from "react-intl";
import HighLightSection, {
  ColoredSection,
} from "../components/highlight-section";
import Layout from "../components/layout";
import { I18Link } from "../components/i18link";
import {
  injectIntlHelperHOC,
  FormattedHTMLMessage,
} from "../components/hooks/use-intlh";
import { BuyNowButtonWithDiscount } from "../components/action-button";
import useQueryParam from "../components/hooks/use-query-param";
import { FormattedDate } from "../components/date";

import { graphql } from "gatsby";
import {
  Header,
  Button,
  Icon,
  Label,
  Message,
  Form,
  Table,
  Input,
  List,
  Popup,
  Statistic,
} from "semantic-ui-react";

import {
  fetchProRenewalPrice,
  ProRenewalCheckout,
  fetchEnterpriseRenewalPrice,
  enterpriseRenewalCheckout,
} from "../utils/store";

import Question from "../components/faq-question";
import { FormQuestion } from "../components/form";
import { upgradeForm } from "../utils/form-actions";
import { parseQuantity } from "../utils/dynamic-pricing";
import dayjs from "dayjs";

import {
  BlackFriday3yRenewalVoucher,
  RenewalBlackFridayBanner,
  BlackFridayContext,
  BlackFriday3yRenewalDiscount,
} from "../components/black-friday/black-friday-context";
import useIntlh from "../components/hooks/use-intlh";

const ActivationKeyInfo = ({ renewalExpiredAt }) => {
  return (
    <div
      style={{
        paddingBottom: "2rem",
      }}
    >
      <span>
        {dayjs().isAfter(dayjs(renewalExpiredAt)) ? (
          <span>
            <FormattedHTMLMessage
              id="upgrade.expired"
              values={{
                date: dayjs(renewalExpiredAt).format("L"),
                duration: dayjs().to(dayjs(renewalExpiredAt)),
              }}
            />
            <br />
            <br />
          </span>
        ) : (
          <span>
            <FormattedHTMLMessage
              id="upgrade.valid"
              values={{
                date: dayjs(renewalExpiredAt).format("L"),
                duration: dayjs().to(dayjs(renewalExpiredAt)),
              }}
            />
            <br />
            <br />
            <strong>
              <FormattedHTMLMessage id="upgrade.upgrade-now" />
            </strong>
          </span>
        )}
      </span>
    </div>
  );
};

// Your maintenance is valid until 02/22/2023. It will expire in 15 days.

const PurchaseRow = ({
  quantity,
  orderRef = "",
  renewalExpiredAt = "",
  locale,
}) => {
  const isBlackFriday = useContext(BlackFridayContext);
  const [pricing, setPricing] = useState({});
  const [pricing2years, setPricing2years] = useState({});
  const [pricing3years, setPricing3years] = useState({});
  const [previousQuantity, setPreviousQuantity] = useState({});
  const [currentQuantity, setQuantity] = useState(quantity);
  const [, intlh] = useIntlh();
  const [endDates, setEndDates] = useState({});

  useEffect(() => {
    let refDate = new Date(renewalExpiredAt);
    const nowDate = new Date();

    if (refDate < nowDate) {
      refDate = nowDate;
    }

    setEndDates({
      oneYear: new dayjs(refDate).add(1, "year").toDate(),
      twoYears: new dayjs(refDate).add(2, "year").toDate(),
      threeYears: new dayjs(refDate).add(3, "year").toDate(),
    });
  }, [renewalExpiredAt]);

  useEffect(() => {
    async function fetchData() {
      if (currentQuantity < 0) {
        setPricing(
          await fetchEnterpriseRenewalPrice(1, {
            optionCode: "SP_RNEW_1_YEARS",
          })
        );
        setPricing2years(
          await fetchEnterpriseRenewalPrice(1, {
            optionCode: "SP_RNEW_2_YEARS",
          })
        );
        setPricing3years(
          await fetchEnterpriseRenewalPrice(1, {
            optionCode: "SP_RNEW_3_YEARS",
          })
        );
      } else {
        setPricing(
          await fetchProRenewalPrice(currentQuantity, {
            optionCode: "SP_RNEW_1_YEARS",
          })
        );
        setPricing2years(
          await fetchProRenewalPrice(currentQuantity, {
            optionCode: "SP_RNEW_2_YEARS",
          })
        );
        setPricing3years(
          await fetchProRenewalPrice(currentQuantity, {
            optionCode: "SP_RNEW_3_YEARS",
            customDiscount: isBlackFriday ? BlackFriday3yRenewalDiscount : 0,
          })
        );
      }
    }
    fetchData();
  }, [currentQuantity, isBlackFriday]);

  useEffect(() => {
    if (quantity !== previousQuantity) {
      setPreviousQuantity(quantity);
      setQuantity(quantity);
    }
  }, [quantity, previousQuantity]);
  return (
    <>
      <Table.Row>
        <Table.Cell>
          <span className="muted-dark">
            <FormattedMessage
              id="upgrade.duration-in-year"
              values={{ count: 1 }}
            />
          </span>
          <br />
          <span class="muted smallest">
            (<FormattedMessage id="upgrade.until" />{" "}
            <FormattedDate locale={locale} value={endDates.oneYear} />)
          </span>
        </Table.Cell>
        <Table.Cell>
          <span className="muted-dark">
            {currentQuantity < 0 ? (
              <FormattedMessage id={"volume-discount.site-license"} />
            ) : (
              <>
                <FormattedMessage id="volume-discount.enter-quantity" />:
                <Input
                  style={{ width: "10rem", marginLeft: "1rem" }}
                  placeholder="Quantity"
                  disabled={true}
                  value={currentQuantity < 0 ? "unlimited" : currentQuantity}
                  onChange={(e) => setQuantity(parseQuantity(e.target.value))}
                />
              </>
            )}
          </span>
        </Table.Cell>
        <Table.Cell>
          <strong>{pricing.totalWithTax}</strong>
          {pricing.tax ? (
            <span className="tax-light muted">
              <br />
              <FormattedMessage
                id="pricing.tax"
                values={{
                  tax: pricing.tax,
                }}
              />
            </span>
          ) : (
            <span>&nbsp;</span>
          )}

          {pricing.isDiscount && (
            <span
              style={{ marginLeft: "0.5rem" }}
              className="strikethrough-light muted"
            >
              {pricing.totalWithoutTaxWithoutDiscount}
            </span>
          )}
          <br />
          {pricing.isDiscount && (
            <span className="saving">
              <FormattedMessage
                id="volume-discount.save"
                values={{ value: pricing.discountTotal }}
              />
            </span>
          )}
        </Table.Cell>
        <Table.Cell>
          {currentQuantity < 0 && (
            <BuyNowButtonWithDiscount
              size="small"
              as="a"
              color="grey"
              href={`${enterpriseRenewalCheckout(1, 1)}${
                orderRef ? `&REF=${encodeURIComponent(orderRef)}` : ""
              }`} // add REF https://knowledgecenter.2checkout.com/Documentation/07Commerce/Checkout-links-and-options/Buy-Link-parameters
            />
          )}
          {currentQuantity > 0 && (
            <BuyNowButtonWithDiscount
              quantity={currentQuantity}
              gain={pricing.isDiscount && pricing.discountTotal}
              size="small"
              as="a"
              color="grey"
              href={`${ProRenewalCheckout(currentQuantity, 1)}${
                orderRef ? `&REF=${encodeURIComponent(orderRef)}` : ""
              }`} // add REF https://knowledgecenter.2checkout.com/Documentation/07Commerce/Checkout-links-and-options/Buy-Link-parameters
            />
          )}
        </Table.Cell>
      </Table.Row>
      <Table.Row>
        <Table.Cell>
          <span className="muted-dark">
            <FormattedMessage
              id="upgrade.duration-in-year"
              values={{ count: 2 }}
            />
          </span>
          <br />
          <span class="muted smallest">
            (<FormattedMessage id="upgrade.until" />{" "}
            <FormattedDate locale={locale} value={endDates.twoYears} />)
          </span>
        </Table.Cell>
        <Table.Cell>
          <span className="muted-dark">
            {currentQuantity < 0 ? (
              <FormattedMessage id={"volume-discount.site-license"} />
            ) : (
              <>
                <FormattedMessage id={"volume-discount.enter-quantity"} />:
                <Input
                  style={{ width: "10rem", marginLeft: "1rem" }}
                  placeholder="Quantity"
                  disabled={true}
                  value={currentQuantity < 0 ? "unlimited" : currentQuantity}
                  onChange={(e) => setQuantity(parseQuantity(e.target.value))}
                />
              </>
            )}
          </span>
        </Table.Cell>
        <Table.Cell>
          <strong>{pricing2years.totalWithTax}</strong>
          {pricing2years.tax ? (
            <span className="tax-light muted">
              <br />
              <FormattedMessage
                id="pricing.tax"
                values={{
                  tax: pricing2years.tax,
                }}
              />
            </span>
          ) : (
            <span>&nbsp;</span>
          )}

          {pricing2years.isDiscount && (
            <span
              style={{ marginLeft: "0.5rem" }}
              className="strikethrough-light muted"
            >
              {pricing2years.totalWithoutTaxWithoutDiscount}
            </span>
          )}
          <br />
          {pricing2years.isDiscount && (
            <span className="saving">
              <FormattedMessage
                id="volume-discount.save"
                values={{ value: pricing2years.discountTotal }}
              />
            </span>
          )}
        </Table.Cell>
        <Table.Cell>
          {currentQuantity < 0 && (
            <BuyNowButtonWithDiscount
              size="small"
              as="a"
              color="grey"
              href={`${enterpriseRenewalCheckout(1, 2)}${
                orderRef ? `&REF=${encodeURIComponent(orderRef)}` : ""
              }`} // add REF https://knowledgecenter.2checkout.com/Documentation/07Commerce/Checkout-links-and-options/Buy-Link-parameters
            />
          )}
          {currentQuantity > 0 && (
            <BuyNowButtonWithDiscount
              quantity={currentQuantity}
              gain={pricing2years.isDiscount && pricing2years.discountTotal}
              size="small"
              as="a"
              color="grey"
              href={`${ProRenewalCheckout(currentQuantity, 2)}${
                orderRef ? `&REF=${encodeURIComponent(orderRef)}` : ""
              }`} // add REF https://knowledgecenter.2checkout.com/Documentation/07Commerce/Checkout-links-and-options/Buy-Link-parameters
            />
          )}
        </Table.Cell>
      </Table.Row>
      <Table.Row>
        <Table.Cell>
          <span className="muted-dark">
            <Label color="orange" horizontal>
              <FormattedMessage id="upgrade.most-popular" />
            </Label>
            <br />
            <strong>
              <FormattedMessage
                id="upgrade.duration-in-year"
                values={{ count: 3 }}
              />
            </strong>
            <br />
            <span class="muted smallest">
              (<FormattedMessage id="upgrade.until" />{" "}
              <FormattedDate locale={locale} value={endDates.threeYears} />)
            </span>
          </span>
        </Table.Cell>
        <Table.Cell>
          <span className="muted-dark"></span>
          {currentQuantity < 0 ? (
            <FormattedMessage id={"volume-discount.site-license"} />
          ) : (
            <>
              <FormattedMessage id="volume-discount.enter-quantity" />:
              <Input
                style={{ width: "10rem", marginLeft: "1rem" }}
                placeholder="Quantity"
                disabled={true}
                value={currentQuantity < 0 ? "unlimited" : currentQuantity}
                onChange={(e) => setQuantity(parseQuantity(e.target.value))}
              />
            </>
          )}
        </Table.Cell>
        <Table.Cell>
          <strong>{pricing3years.totalWithTax}</strong>
          {pricing3years.tax ? (
            <span className="tax-light muted">
              <br />
              <FormattedMessage
                id="pricing.tax"
                values={{
                  tax: pricing3years.tax,
                }}
              />
            </span>
          ) : (
            <span>&nbsp;</span>
          )}

          {pricing3years.isDiscount && (
            <span
              style={{ marginLeft: "0.5rem" }}
              className="strikethrough-light muted"
            >
              {pricing3years.totalWithoutTaxWithoutDiscount}
            </span>
          )}
          <br />
          {pricing3years.isDiscount && (
            <span className="saving">
              <FormattedMessage
                id="volume-discount.save"
                values={{ value: pricing3years.discountTotal }}
              />
            </span>
          )}

          {isBlackFriday &&
            currentQuantity > 0 /* no offer for enterprise renewal */ && (
              <>
                <div
                  style={{ fontSize: "smaller" }}
                  dangerouslySetInnerHTML={{
                    __html: intlh("pricing.discount-with-voucher", {
                      voucher: BlackFriday3yRenewalVoucher,
                    }),
                  }}
                ></div>
              </>
            )}
        </Table.Cell>
        <Table.Cell>
          {currentQuantity < 0 && (
            <BuyNowButtonWithDiscount
              size="small"
              as="a"
              color="green"
              href={`${enterpriseRenewalCheckout(1, 3)}${
                orderRef ? `&REF=${encodeURIComponent(orderRef)}` : ""
              }`} // add REF https://knowledgecenter.2checkout.com/Documentation/07Commerce/Checkout-links-and-options/Buy-Link-parameters
            />
          )}
          {currentQuantity > 0 && (
            <BuyNowButtonWithDiscount
              quantity={currentQuantity}
              gain={pricing3years.isDiscount && pricing3years.discountTotal}
              size="small"
              as="a"
              href={`${ProRenewalCheckout(currentQuantity, 3, {
                voucher: isBlackFriday
                  ? BlackFriday3yRenewalVoucher
                  : undefined,
              })}${orderRef ? `&REF=${encodeURIComponent(orderRef)}` : ""}`} // add REF https://knowledgecenter.2checkout.com/Documentation/07Commerce/Checkout-links-and-options/Buy-Link-parameters
            />
          )}
        </Table.Cell>
      </Table.Row>
    </>
  );
};

const UpgradeForm = injectIntlHelperHOC(({ intlh, locale }) => {
  let questionIndex = 1;
  const [loading, setLoading] = useState(false);
  const [keyDetails, setKeyDetails] = useState();
  const [errorMessages, setErrorMessages] = useState([]);

  const [formData, setFormData] = useState({});
  const [keyFromUrl, setKeyFromUrl] = useQueryParam("key");

  const isError = errorMessages && errorMessages.length > 0;

  const handleChange = (e, { name, value, checked }) => {
    setKeyDetails(undefined);
    setFormData((previous) => ({
      ...previous,
      [name]: value !== undefined ? value : checked,
      errors: undefined,
    }));
  };

  const resetForm = () => {
    setFormData({});
    setKeyDetails(undefined);
    setKeyFromUrl(undefined);
    setErrorMessages([]);
  };

  const searchKey = async ({ key }) => {
    // Sanitize the key
    const sanitizedKey = key
      ? key
          .replace(/\t|\n|\r|"|'|”|“/g, "")
          .replace(/^\s+/g, "")
          .replace(/\s+$/g, "")
      : key;
    setKeyDetails(undefined);
    setLoading(true);
    // const ret = await
    upgradeForm({ key: sanitizedKey })
      .then((details) => {
        if (details.activationStatus !== "WORKING") {
          throw new Error(
            `Invalid activation status: ${details.activationStatus}`
          );
        }
        setKeyDetails(details);
        setFormData((previous) => ({
          ...previous,
          key: sanitizedKey,
          errors: undefined,
        }));
        setErrorMessages([]);
      })
      .catch(() => {
        setKeyDetails(undefined);
        setFormData((previous) => ({
          ...previous,
          key: sanitizedKey,
          errors: { key: true },
        }));
        setErrorMessages([intlh("upgrade.invalid-key")]);
      })
      .then(() => {
        setLoading(false);
      });
  };

  const searchKeyMemoized = useCallback(searchKey, [intlh]);

  const handleSubmit = async () => searchKeyMemoized(formData);

  // Use effect otherwise reconciliation issues occur
  useEffect(() => {
    if (keyFromUrl && formData.key === undefined) {
      // First time => update and submit the form
      setFormData({ key: keyFromUrl });
      searchKeyMemoized({ key: keyFromUrl });
    }
  }, [keyFromUrl, formData.key, searchKeyMemoized]);

  // synchronize the url with the current form
  useEffect(() => {
    setKeyFromUrl(formData.key);
  }, [formData.key, setKeyFromUrl]);

  return (
    <Form
      success
      onSubmit={handleSubmit}
      id="lost_key_form"
      error={isError}
      name="lost_key_form"
      className="survey"
    >
      <Form.Group grouped>
        <Form.Input
          size="mini"
          label={
            <FormQuestion
              intlId="upgrade.enter-your-key"
              index={questionIndex++}
            />
          }
          placeholder="[0/3]-[screenpressopro]..."
          name="key"
          autoFocus
          value={formData.key || ""}
          error={formData.errors && formData.errors.key}
          onChange={handleChange}
          onKeyDown={(e) => {
            if (e.keyCode === 13) {
              // Enter key
              e.preventDefault();
              handleSubmit();
            }
          }}
        >
          <input />
          <Button
            icon
            onClick={(e) => {
              e.preventDefault();
              resetForm();
            }}
          >
            <Icon name="delete" />
          </Button>
        </Form.Input>
        <Label>
          <I18Link id="pricing">
            <FormattedMessage id={"upgrade.buy-new-one"} />
          </I18Link>
        </Label>
        <Label>
          <I18Link id="lost-key">
            <FormattedMessage id={"upgrade.lost-your-key"} />
          </I18Link>
        </Label>
        <Label>
          <I18Link
            id="which-versions-for-my-key"
            autotext
            query={
              formData.key ? `?key=${encodeURIComponent(formData.key)}` : ""
            }
          ></I18Link>
        </Label>
        {!keyDetails && (
          <Form.Field style={{ textAlign: "right" }}>
            <Button
              loading={loading}
              disabled={loading}
              color="blue"
              size="large"
              icon
            >
              <Button.Content>
                <Icon name="check circle outline" />
                <FormattedMessage id="upgrade.validate" />
              </Button.Content>
            </Button>
          </Form.Field>
        )}
      </Form.Group>

      <Message size="small" error list={errorMessages ? errorMessages : []} />

      {keyDetails && (
        <>
          <div>
            <Form.Field>
              <FormQuestion
                intlId="upgrade.about-your-key"
                index={questionIndex++}
              />
            </Form.Field>

            <ActivationKeyInfo renewalExpiredAt={keyDetails.renewalExpiredAt} />
          </div>
          <div>
            <Form.Field>
              <FormQuestion
                intlId="upgrade.upgrade-plan"
                index={questionIndex++}
              />
            </Form.Field>
            <Table celled padded>
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell>
                    <FormattedMessage id="upgrade.duration" />
                  </Table.HeaderCell>
                  <Table.HeaderCell>
                    <FormattedMessage id="volume-discount.quantity" />
                  </Table.HeaderCell>
                  <Table.HeaderCell>
                    <FormattedMessage id="upgrade.renewal-price" />
                  </Table.HeaderCell>
                  <Table.HeaderCell>
                    <FormattedMessage id="volume-discount.buy" />
                  </Table.HeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                <PurchaseRow
                  quantity={keyDetails.quantity}
                  orderRef={keyDetails.keyId}
                  renewalExpiredAt={keyDetails.renewalExpiredAt}
                  locale={locale}
                />
              </Table.Body>
            </Table>
          </div>
        </>
      )}
    </Form>
  );
});

const semanticColorFromCode = (name) => {
  switch (name) {
    case "blue":
      return "#2185D0";
    case "red":
      return "#DB2828";
    case "green":
      return "#21BA45";
    default:
      return "#000000";
  }
};

const RegularPrice = ({ price, color, tax }) => (
  <Statistic color={color} size="small">
    <Statistic.Value>{price}</Statistic.Value>
    {tax && (
      <div style={{ color: semanticColorFromCode(color), fontSize: "smaller" }}>
        <FormattedMessage
          id="pricing.tax"
          values={{
            tax,
          }}
        />
      </div>
    )}
  </Statistic>
);

const SinglePrice = ({ quantity, price, regularPrice, saving }) => (
  <span>
    <strong>
      <FormattedMessage
        id="volume-discount.license-pack"
        values={{ count: quantity }}
      />
    </strong>
    :<span style={{ marginLeft: "0.5rem" }}>{price}</span>
    <div>
      {saving && (
        <span
          style={{ marginLeft: "0.5rem" }}
          className="strikethrough-light muted"
        >
          {regularPrice}
        </span>
      )}

      {saving && (
        <span style={{ marginLeft: "0.5rem" }} className="saving">
          <FormattedMessage
            id="volume-discount.save"
            values={{ value: saving }}
          />
        </span>
      )}
    </div>
  </span>
);

const Pricing = injectIntlHelperHOC(({ intlh }) => {
  const quantities = [1, 2, 5, 10];
  const [proPricing, setProPricing] = useState(
    quantities.map((quantity) => ({ quantity }))
  );

  const [enterprisePricing, setEnterprisePricing] = useState({});

  useEffect(() => {
    async function fetchData() {
      setEnterprisePricing(await fetchEnterpriseRenewalPrice());
    }
    fetchData();
  }, []);

  useEffect(() => {
    async function fetchData() {
      setProPricing(
        await Promise.all(quantities.map((q) => fetchProRenewalPrice(q)))
      );
    }
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...quantities]);

  return (
    <div>
      <List>
        <List.Item style={{ marginBottom: "0.5rem" }}>
          <FormattedMessage
            id="upgrade.pro"
            values={{
              name: "Screenpresso PRO",
              price: proPricing[0].totalWithTax,
            }}
          />
          &nbsp;
          <span className="muted smaller">
            <FormattedMessage
              id="upgrade.tax"
              values={{
                tax: proPricing[0].tax,
              }}
            />
          </span>
          ,&nbsp;
          <Popup
            trigger={
              <a href="#">
                <FormattedMessage id="upgrade.volume-discount" />
              </a>
            }
          >
            <List>
              {proPricing.map((p, i) => (
                <List.Item
                  style={{ marginBottom: "0.5rem" }}
                  key={`price_for_${i}_users`}
                >
                  <SinglePrice
                    key={`price_for_${i}_users`}
                    quantity={p.quantity}
                    price={p.totalWithTax}
                    regularPrice={p.totalWithTaxWithoutDiscount}
                    saving={p.isDiscount && p.discountTotal}
                  />
                </List.Item>
              ))}
            </List>
          </Popup>
        </List.Item>
        <List.Item>
          <FormattedMessage
            id="upgrade.pro"
            values={{
              name: "Screenpresso ENTERPRISE",
              price: enterprisePricing.totalWithoutTax,
            }}
          />
          &nbsp;
          <span className="muted smaller">
            <FormattedMessage
              id="upgrade.extax"
              values={{
                tax: enterprisePricing.tax,
              }}
            />
            .
          </span>
        </List.Item>
      </List>
    </div>
  );
});

const UpgradePage = ({ pageContext: { language, title, id }, data }) => {
  return (
    <Layout locale={language} title={title} pageId={id}>
      <HighLightSection
        title={<FormattedMessage id="upgrade.title" />}
        subTitle={<FormattedMessage id="upgrade.sub-title" />}
      />
      <RenewalBlackFridayBanner product="Screenpresso PRO" />
      <Pricing />
      <UpgradeForm locale={language} />
      <ColoredSection>
        <Header as="h1">
          <FormattedMessage id="pricing.faq" />
        </Header>
        {data.faqEntries &&
          data.faqEntries.edges
            .filter((question) => question.node.fields.id !== "how-to-activate")
            .map((question) => (
              <Question key={`q_${question.node.id}`} node={question.node} />
            ))}
      </ColoredSection>
    </Layout>
  );
};

export default UpgradePage;

export const query = graphql`
  query UpgradeQuery($language: String!) {
    faqEntries: allMarkdownRemark(
      filter: {
        frontmatter: { tags: { eq: "upgrade" } }
        fields: { language: { eq: $language } }
      }
    ) {
      edges {
        node {
          id
          htmlAst
          frontmatter {
            title
          }
          fields {
            id
          }
        }
      }
    }
  }
`;
